import { useState } from 'react';

/**
 * The cart line's thumbnail, with the same larger preview on hover that the
 * admin product list gives its rows (`catalog/RowImage.tsx`) — same size, same
 * placement, same `.mk-rowimg-pop` styling — so a buyer can see the piece
 * clearly without opening its detail page.
 *
 * A separate component rather than `RowImage` itself: the cart keeps its own
 * 38px `.mk-thumb` frame (the row layout is unchanged), and only the preview
 * behaviour is shared.
 *
 * The preview is `position: fixed`, placed from the thumbnail's own rectangle,
 * for the reason `RowImage` gives: the table sits inside `.mk-twrap`
 * (`overflow-x: auto`), which clips anything absolutely positioned inside it —
 * vertically too — so an absolute popup would be drawn and then cut off.
 */
export function ThumbPreview({ url, label }: { url: string | null | undefined; label: string }) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  if (!url) return <span className="mk-thumb" />;

  const open = (el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const SIZE = 300;
    // Kept on screen: below the fold it flips upward, and near the right edge it
    // opens to the left of the thumbnail instead.
    const top = Math.min(Math.max(8, r.top + r.height / 2 - SIZE / 2), window.innerHeight - SIZE - 8);
    const left = r.right + 12 + SIZE > window.innerWidth ? r.left - SIZE - 12 : r.right + 12;
    setAt({ top, left });
  };

  return (
    <span
      className="mk-thumb mk-thumb-preview"
      tabIndex={0}
      onMouseEnter={(e) => open(e.currentTarget)}
      onMouseLeave={() => setAt(null)}
      onFocus={(e) => open(e.currentTarget)}
      onBlur={() => setAt(null)}
    >
      <img src={url} alt="" />
      {at ? (
        <span className="mk-rowimg-pop" style={{ top: at.top, left: at.left }} aria-hidden="true">
          <img src={url} alt="" />
          <span>{label}</span>
        </span>
      ) : null}
    </span>
  );
}
